import { makeStyles } from '@material-ui/core';
import { Autocomplete, AutocompleteProps } from '@material-ui/lab';
import React from 'react';
import { debounce } from '../../util/event';
import { TextField } from './';

const useSearchBarClasses = makeStyles({
    listbox: {
        padding: 0,
    },
    loading: {
        padding: 0,
    },
    option: {
        padding: 0,
    },
});

/**
 * Global Search bar component.
 *
 * Different from other global components, the search bar does not allow internal components customization due to
 * interactions between the `Autocomplete` and `TextField` components.
 *
 * @param props.className search bar classes
 * @param props.label search bar label
 * @param props.options list of options to be displayed
 * @param props.filterOptions function to filter the current options given the query and options.
 *        The `'no-filter'` flag can be used to prevent the default filter implemented by the autocomplete component.
 *        The default filter use strings created by `props.renderLabel` to filter the options
 * @param props.debounce debounce calls to `props.onSearch` when the user is typing
 * @param props.loading if the search bar is loading (renders `props.renderLoading`)
 * @param props.loadingIgnoreOptions ignore any options if the loading flag is set
 * @param props.renderLoadingSpinner content rendered when loading
 * @param props.renderLoading content rendered when loading
 * @param props.renderEmpty content rendered when empty
 * @param props.renderOption function to generate the content to be rendered for each option
 * @param props.renderLabel function to create a search bar label for a chosen option
 * @param props.loadingIgnoreOptions ignore any options if the loading flag is set
 * @param props.filterOptions function to filter the current options given the query and options, the `'no-filter'` flag
 *        can be used to prevent the default filter implemented by material-ui autocomplete component.
 * @param props.renderInput function to render a customized input insed Search bar
 * @param props.debounce debounce calls to `props.onSearch` when the user is typing
 * @param props.onSearch callback called when the user types something in the search bar
 * @param props.onSelect callback called when the user choses one of the displayed options, or clear the input
 */
export const SearchBar = <T extends any>(props: {
    className?: string;
    label?: string;
    options: T[];
    loading?: boolean;
    renderLoadingSpinner?: boolean;
    renderLoading?: React.ReactNode;
    renderEmpty?: React.ReactNode;
    renderOption?: (option: T) => React.ReactNode;
    renderLabel?: (option: T) => string;
    loadingIgnoreOptions?: boolean;
    filterOptions?: AutocompleteProps<T, false, false, false>['filterOptions'] | 'no-filter';
    debounce?: number;
    onSearch?: (query: string) => void;
    onSelect?: (option?: T) => void;
}) => {
    const classes = useSearchBarClasses();
    const [open, setOpen] = React.useState(false);
    const onSearch = React.useCallback(debounce(props.onSearch, props.debounce ?? 0), [props.onSearch, props.debounce]);

    return (
        <Autocomplete
            className={props.className}
            classes={{ ...classes }}
            open={open}
            onOpen={() => setOpen(true)}
            onClose={() => setOpen(false)}
            loading={props.loading}
            options={props.loading && props.loadingIgnoreOptions ? [] : props.options}
            loadingText={props.renderLoading}
            noOptionsText={props.renderEmpty}
            renderOption={option => props.renderOption?.(option) ?? String(option)}
            getOptionLabel={option => props.renderLabel?.(option) ?? String(option)}
            filterOptions={props.filterOptions === 'no-filter' ? options => options : props.filterOptions}
            onChange={(event, value) => props.onSelect?.(value ?? undefined)}
            renderInput={inputProps => (
                <TextField {...inputProps} label={props.label} onChange={event => onSearch(event.target.value)} />
            )}
        />
    );
};
